import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plug } from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Button } from '../../components/ui/Button'
import { TextField, FieldLabel } from '../../components/ui/Field'
import { api, extractApiError } from '../../lib/api'
import { lengthError } from '../../lib/fieldLength'
import { formatDateTime } from '../../lib/format'
import { SOP_AUTH_TYPES, sopConfig, sopTestResult } from '../../lib/types'

const AUTH_LABEL= {
  none: 'No authentication',
  api_key: 'API key (sent as X-API-Key header)',
  bearer: 'Bearer token (sent as Authorization: Bearer <token>)',
}

export function SopIntegrationPage() {
  const qc = useQueryClient()
  const [endpointUrl, setEndpointUrl] = useState('')
  // Optional per-SOP detail URL template — empty by default. Supports
  // `{slug}` / `{id}` placeholders, e.g.
  // `https://api.sopgalaxy.com/v1/sops/{slug}`. When blank the backend
  // falls back to guessing by appending the identifier to
  // `endpointUrl`; when set, that one URL is used verbatim.
  const [detailUrlTemplate, setDetailUrlTemplate] = useState('')
  const [authType, setAuthType] = useState('none')
  const [credential, setCredential] = useState('')
  const [credentialDirty, setCredentialDirty] = useState(false)

  const configQuery = useQuery({
    queryKey: ['super-admin', 'sop-config'],
    queryFn: async () => {
      const res = await api.get('/super-admin/integrations/sop')
      return sopConfig.parse(res.data)
    },
  })

  // Sync the form state to the loaded config the first time it arrives or
  // whenever it's refetched.
  useEffect(() => {
    if (!configQuery.data) return
    setEndpointUrl(configQuery.data.endpointUrl)
    setDetailUrlTemplate(configQuery.data.detailUrlTemplate ?? '')
    setAuthType(configQuery.data.authType)
    setCredential('')
    setCredentialDirty(false)
  }, [configQuery.data])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body= {
        endpointUrl: endpointUrl.trim(),
        detailUrlTemplate: detailUrlTemplate.trim(),
        authType,
      }
      // Only send a credential when the user has typed something. Empty +
      // dirty = explicit clear; empty + clean = leave as-is.
      if (credentialDirty) body.credential = credential
      await api.put('/super-admin/integrations/sop', body)
    },
    onSuccess: () => {
      toast.success('SOP integration saved successfully.')
      void qc.invalidateQueries({ queryKey: ['super-admin', 'sop-config'] })
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      toast.error(fields?.endpointUrl ?? fields?.credential ?? message)
    },
  })

  const testMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/super-admin/integrations/sop/test')
      return sopTestResult.parse(res.data)
    },
    onSuccess: (result) => {
      if (result.ok) {
        toast.success(
          `${result.message} HTTP ${result.status}, ${result.latencyMs}ms.`,
        )
      } else {
        toast.error(`${result.message} (${result.latencyMs}ms)`)
      }
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const hasStoredCred = Boolean(configQuery.data?.hasCredential)
  const credentialPlaceholder =
    authType === 'none'
      ? 'No credential needed.'
      : hasStoredCred && !credentialDirty
        ? '•••••••••••• already saved (leave blank to keep)'
        : authType === 'api_key'
          ? 'Paste the SOP API key'
          : 'Paste the bearer token'

  return (
    // The page is mounted inside SuperAdminLayout — that layout owns
    // the sidebar, the topbar with the UserMenu, and the canvas
    // background. Previously this page rendered its own `min-h-screen`
    // wrapper, its own header, and a second UserMenu, which doubled
    // the user pill and back-link inside the layout's content area.
    // Now it renders content only.
    <div className="max-w-3xl mx-auto space-y-6">
      <Breadcrumb current="SOP Integration" homePath="/super-admin/dashboard" />

      <div>
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <Plug
            size={20}
            className="text-[color:var(--color-accent-strong)]"
          />
          SOP Integration
        </h1>
        <p className="text-sm text-[color:var(--color-ink-muted)] mt-1">
          Connect your SOP library to tasks.
        </p>
      </div>

      {configQuery.isLoading ? (
          <p className="text-sm text-[color:var(--color-ink-muted)]">Loading configuration…</p>
        ) : (
          <>
            <section className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 space-y-4">
              <TextField
                name="endpointUrl"
                label="Endpoint URL"
                required
                placeholder="Enter the Endpoint URL"
                value={endpointUrl}
                onChange={(e) => setEndpointUrl(e.target.value)}
                maxLength={500}
              />

              <div>
                <FieldLabel
                  label="SOP Detail URL Template"
                  htmlFor="sop-detail-url-template"
                />
                <input
                  id="sop-detail-url-template"
                  type="text"
                  value={detailUrlTemplate}
                  onChange={(e) => setDetailUrlTemplate(e.target.value)}
                  placeholder="https://api.example.com/sops/{slug}"
                  /* Soft limit: accept past 500 and flag it (below); save is
                     capped by the server. */
                  className={
                    'w-full h-10 px-3 rounded-md border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] ' +
                    (lengthError(detailUrlTemplate, undefined, 500, 'SOP Detail URL Template')
                      ? 'border-[color:var(--color-priority-urgent)]'
                      : 'border-[color:var(--color-rule)]')
                  }
                />
                {lengthError(detailUrlTemplate, undefined, 500, 'SOP Detail URL Template') ? (
                  <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                    {lengthError(detailUrlTemplate, undefined, 500, 'SOP Detail URL Template')}
                  </p>
                ) : null}
                <p className="mt-1.5 text-xs text-[color:var(--color-ink-muted)]">
                  Optional. URL TaskMagix hits when a user opens an SOP card —
                  use <code>{'{slug}'}</code> or <code>{'{id}'}</code> as the
                  placeholder. Leave blank and we'll try a few common patterns.
                </p>
              </div>

              <div>
                <FieldLabel label="Authentication" htmlFor="sop-auth-type" />
                <select
                  id="sop-auth-type"
                  value={authType}
                  onChange={(e) => setAuthType(e.target.value )}
                  className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
                >
                  {SOP_AUTH_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {AUTH_LABEL[t]}
                    </option>
                  ))}
                </select>
              </div>

              {authType !== 'none' ? (
                <div>
                  <FieldLabel label="Credential" htmlFor="sop-credential" />
                  <input
                    id="sop-credential"
                    type="password"
                    autoComplete="off"
                    placeholder={credentialPlaceholder}
                    value={credential}
                    onChange={(e) => {
                      setCredential(e.target.value)
                      setCredentialDirty(true)
                    }}
                    maxLength={2000}
                    className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
                  />
                  <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                    Encrypted at rest with AES-256-GCM. Leave the field blank to keep the
                    saved credential; clear it explicitly to remove.
                  </p>
                </div>
              ) : null}
            </section>

            <div className="flex items-center gap-3">
              <Button
                onClick={() => saveMutation.mutate()}
                loading={saveMutation.isPending}
                disabled={!endpointUrl.trim()}
              >
                Save
              </Button>
              <Button
                variant="secondary"
                onClick={() => testMutation.mutate()}
                loading={testMutation.isPending}
                disabled={!configQuery.data || !configQuery.data.endpointUrl}
                title={
                  !configQuery.data || !configQuery.data.endpointUrl
                    ? 'Save the endpoint URL first.'
                    : 'Send a GET against the configured endpoint.'
                }
              >
                Test Connection
              </Button>
              {configQuery.data?.updatedAt ? (
                <span className="ml-auto text-xs text-[color:var(--color-ink-muted)]">
                  Last updated {formatDateTime(configQuery.data.updatedAt)}
                </span>
              ) : null}
            </div>

            {testMutation.data ? (
              <section className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 text-sm">
                <p className="font-semibold mb-2">
                  {testMutation.data.ok ? 'Last test: success' : 'Last test: failed'}
                </p>
                <ul className="space-y-1 text-xs text-[color:var(--color-ink-muted)]">
                  <li>Status: HTTP {testMutation.data.status}</li>
                  <li>Latency: {testMutation.data.latencyMs}ms</li>
                  <li>Message: {testMutation.data.message}</li>
                  {testMutation.data.bodySnippet ? (
                    <li>
                      <pre className="mt-2 whitespace-pre-wrap break-words rounded border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-2">
                        {testMutation.data.bodySnippet}
                      </pre>
                    </li>
                  ) : null}
                </ul>
              </section>
            ) : null}
          </>
        )}
    </div>
  )
}
